import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../../components/PageConfigProvider/constants';
import type { TabContent } from '../../components/PageConfigProvider/page-reducer';
import type {
  SubsidiarySnapshotKind,
  SubsidiaryTrendKind,
} from '../../components/PageConfigProvider/types/item';

const SIDE_COLUMN_WIDTH = DEFAULT_GRID_COLUMNS / 4;
const MAIN_WIDTH = DEFAULT_GRID_COLUMNS - SIDE_COLUMN_WIDTH;
const SIDE_CARD_COUNT = 3;
const SIDE_CARD_HEIGHT = DEFAULT_GRID_ROWS / SIDE_CARD_COUNT;

export const SUBSIDIARY_CHART_POSITION = {
  x: 0,
  y: 0,
  width: MAIN_WIDTH,
  height: DEFAULT_GRID_ROWS,
};

const sideCardPosition = (row: number) => ({
  x: MAIN_WIDTH,
  y: row * SIDE_CARD_HEIGHT,
  width: SIDE_COLUMN_WIDTH,
  height: SIDE_CARD_HEIGHT,
});

const ACTIVITY_TITLE = 'Acquisitions & Divestitures';

const CONCENTRATION_SCALE =
  'Herfindahl–Hirschman index: 25% for four equal subsidiaries, 100% for one.';

const SNAPSHOT_CARDS: {
  kind: SubsidiarySnapshotKind;
  title: string;
  info: string;
}[] = [
  {
    kind: 'concentration',
    title: 'Concentration',
    info: `How concentrated the staff is across direct subsidiaries, using today’s headcounts. ${CONCENTRATION_SCALE}`,
  },
  {
    kind: 'activity',
    title: ACTIVITY_TITLE,
    info: 'Subsidiaries acquired and divested over the group’s history, or up to the selected date.',
  },
];

export const subsidiarySnapshotCards = (
  idPrefix: string
): TabContent['items'] => [
  {
    id: `${idPrefix}-subsidiary-count`,
    title: 'Subsidiaries',
    isTitleManuallySet: true,
    info: {
      text: 'Companies under the selected company, at any depth, today or on the selected date.',
    },
    position: sideCardPosition(0),
    plot: {
      chartType: 'bar' as const,
      adaptive: true,
      defaultMetricOptions: ['subsidiary_count' as const],
    },
  },
  ...SNAPSHOT_CARDS.map((card, index) => ({
    id: `${idPrefix}-${card.kind}`,
    title: card.title,
    isTitleManuallySet: true,
    info: { text: card.info },
    position: sideCardPosition(index + 1),
    plot: {
      chartType: 'subsidiary-snapshot' as const,
      defaultMetricOptions: ['subsidiary_count' as const],
      subsidiarySnapshot: card.kind,
    },
  })),
];

const TREND_CARDS: {
  kind: SubsidiaryTrendKind;
  title: string;
  info: string;
}[] = [
  {
    kind: 'count',
    title: 'Subsidiaries',
    info: 'Companies in the group on each date, at any depth.',
  },
  {
    kind: 'concentration',
    title: 'Concentration',
    info: `Concentration on each date, using today’s headcounts: it moves as subsidiaries join or leave. ${CONCENTRATION_SCALE}`,
  },
  {
    kind: 'activity',
    title: ACTIVITY_TITLE,
    info: 'Subsidiaries acquired and divested each year. With several companies: net acquisitions (acquired minus divested). Hover a year to see its companies on the timeline.',
  },
];

export const subsidiaryTrendCards = (idPrefix: string): TabContent['items'] =>
  TREND_CARDS.map((card, index) => ({
    id: `${idPrefix}-${card.kind}`,
    title: card.title,
    isTitleManuallySet: true,
    info: { text: card.info },
    position: sideCardPosition(index),
    plot: {
      chartType: 'subsidiary-trend' as const,
      defaultMetricOptions: ['subsidiary_count' as const],
      subsidiaryTrend: card.kind,
    },
  }));
